import fineGoldImage from 'images/gold-with-shadow-cropped.png'
import finePlatinumImage from 'images/platinum-with-shadow-cropped.png'
import fineSilverImage from 'images/silver-with-shadow-cropped.png'

import { useMemo } from 'react'
import { useParams } from 'react-router'
import { useQuery } from 'react-query'
import { fetchProducts, Product } from 'api/inventory'
import { useAuth } from 'components/AuthProvider'
import { useClientConfig } from 'hooks/useClientConfig'
import { usePerformanceSummaries } from 'hooks/useInventory'

export type ProductInclLocal = Product & {
  // False for a product the customer may hold and sell but not buy: platinum
  // they already own while the `platinum_trading` flag is off.
  buyable: boolean
  disable: boolean
  image: string
  descriptions: string[]
  shortDescription: string
  sliderScalingFactor?: number
  termsAcceptanceLink?: string
}

export const localProductsData: Record<
  string,
  {
    image: string
    shortDescription: string
    descriptionParagraphs: string[]
    disable: boolean
    sliderScalingFactor?: number
    termsAcceptanceLink?: string
  }
> = {
  gold: {
    image: fineGoldImage,
    shortDescription: '99.99% fine Gold',
    descriptionParagraphs: [
      `Once tethered to the value of currency, investment gold has
      become the benchmark for appreciating wealth. The gold bar you
      see here is the exact bar of gold we have stored at the Goldie
      vault in Wellington.`,
      `First found in 2450 BC by the Ancient Egyptians, it has ever
      since been a modus for commodities exchange and wealth. You
      may have heard the phrase "gold standard". In the 20th
      century, banks and governments used the price of gold to
      benchmark the currency markets. At the time, this helped
      tether inflation but made it harder to stimulate the economy
      because any monetary stimulus had to stay in lockstep with the
      market price for gold.`,
      `To get a good market price when buying and selling gold, you
      generally need to buy a lot of gold all at once. This is
      called the wholesale market rate. To get the wholesale price
      of gold, you would need to buy 1kg* of gold which at the time
      of writing costs $103,402.27*. This is unattainable to almost
      everyone in New Zealand. If you wish to buy smaller amounts of
      gold, by the time you pay a higher market rate for a smaller
      amount of gold, and pay commission to the broker, it makes it
      much harder to make a return on the investment.`
    ],
    termsAcceptanceLink: '',
    disable: false
  },
  silver: {
    image: fineSilverImage,
    shortDescription: '99.9% pure Silver',
    descriptionParagraphs: [
      `Silver has long been valued as a precious metal. Silver metal
      is used in many bullion coins, sometimes alongside gold`,
      `Silver was one of the seven metals of antiquity that were
      known to prehistoric humans and whose discovery is thus lost
      to history. In particular, the three metals of group 11,
      copper, silver, and gold, occur in the elemental form in
      nature and were probably used as the first primitive forms of
      money as opposed to simple bartering. However, unlike copper,
      silver did not lead to the growth of metallurgy on account of
      its low structural strength, and was more often used
      ornamentally or as money. Since silver is more reactive than
      gold, supplies of native silver were much more limited than
      those of gold. For example, silver was more expensive than
      gold in Egypt until around the fifteenth century BC: the
      Egyptians are thought to have separated gold from silver by
      heating the metals with salt, and then reducing the silver
      chloride produced to the metal.`,
      `Other than in currency and as an investment medium (coins and
      bullion), silver is used in solar panels, water filtration,
      jewellery, ornaments, high-value tableware and utensils (hence
      the term "silverware"), in electrical contacts and conductors,
      in specialized mirrors, window coatings, in catalysis of
      chemical reactions, as a colorant in stained glass, and in
      specialized confectionery. Its compounds are used in
      photographic and X-ray film`
    ],
    termsAcceptanceLink: '',
    disable: false,
    sliderScalingFactor: 10
  },
  platinum: {
    image: finePlatinumImage,
    shortDescription: '99.95% fine Platinum',
    descriptionParagraphs: [
      `Platinum is one of the rarest precious metals on earth. Fewer
      than 200 tonnes are mined each year, around one twentieth of the
      gold produced, and about three quarters of it comes from South
      Africa. The platinum bar you see here is the kind of 1kg PAMP
      Suisse bar of 99.95% fine platinum we store at the Goldie vault
      in Wellington.`,
      `More than 2,000 years ago, the La Tolita-Tumaco metalsmiths of
      present-day Ecuador and Colombia fused platinum with gold to
      make jewellery and fine tools, long before Europeans could work
      the metal. Spanish explorers later found it mixed into the river
      gold of Colombia's Chocó region, called it “platina”, or “little
      silver”, and dismissed it as a nuisance. Antonio de Ulloa
      described it to European science in 1748, and by the 19th
      century it was trusted so completely for its stability that the
      international prototype kilogram, made of platinum and iridium
      in 1889, defined the world's kilogram for 130 years.`,
      `Today platinum is prized for being dense, highly resistant to
      corrosion and an exceptional catalyst. Its largest single use is
      in catalytic converters that clean vehicle exhaust, and it is
      also used in hydrogen fuel cells, medical devices, electronics
      and fine jewellery. Because so much of its demand is industrial,
      platinum's price can move quite differently from gold's. It was
      priced above gold for most of the 1990s and 2000s, peaking at
      over US$2,270 an ounce in 2008, has been priced below gold since
      January 2015, and set a new record above US$2,800 an ounce in
      January 2026.`,
      `As with gold, the best price for platinum is the wholesale rate,
      and that usually means buying a whole 1kg bar at once, which is
      out of reach for almost everyone in New Zealand. With Goldie you
      can own a portion of a real platinum bar, stored and insured at
      our vault in Wellington.`
    ],
    termsAcceptanceLink: '',
    disable: false,
    // Platinum trades at roughly 40% of gold's price per gram, so the slider
    // reaches ~2.5x the weight of gold (125g vs 50g) for a similar dollar range.
    sliderScalingFactor: 2.5
  }
}

// Platinum is gated behind the backend `platinum_trading` feature flag. Its
// product id is not fixed across environments, so it is recognised by name or
// SKU — never by id.
export function isPlatinumProduct(
  product: Pick<Product, 'name' | 'sku'>
): boolean {
  return (
    product.name?.toLowerCase() === 'platinum' ||
    product.sku?.toUpperCase() === 'PLAT001'
  )
}

function withLocals(p: Product, buyable: boolean) {
  const local = localProductsData[p.name.toLowerCase()]
  return {
    ...p,
    buyable,
    disable: local?.disable,
    image: local?.image,
    descriptions: local?.descriptionParagraphs,
    shortDescription: local?.shortDescription,
    slidingScalingFactor: local?.sliderScalingFactor
  }
}

export default function useProducts(forceProductId = null) {
  const { data: products, isLoading: productsLoading } = useQuery(
    ['products'],
    fetchProducts,
    { staleTime: 300_000 }
  )

  // The backend already omits platinum from /products when the flag is off;
  // this is defence in depth. Absent, loading or errored config means OFF.
  const { data: clientConfig, isLoading: configLoading } = useClientConfig()
  const platinumTradingEnabled =
    clientConfig?.features?.platinum_trading ?? false

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const params = useParams<any>()
  const productId = Number(forceProductId ?? params?.productId ?? 1)

  // Sells are never gated: a customer who already holds platinum keeps seeing
  // it (vault card, trade page, sell) with the flag off, just not buying it.
  // /products omits it then, so it is taken from the active vault's performance
  // summaries, which still carry a row for held metal. Same query key as My
  // Vault, so the two share one request.
  const { user } = useAuth()
  const activatedVaultId = Number(user?.activatedVault?.id)
  const { data: summaries, isLoading: summariesLoading } =
    usePerformanceSummaries(
      Number.isFinite(activatedVaultId) ? activatedVaultId : undefined,
      { enabled: Boolean(user) && !configLoading && !platinumTradingEnabled }
    )

  const productsInclLocals = useMemo(() => {
    if (!products) return undefined
    const offered = products.filter(
      p => platinumTradingEnabled || !isPlatinumProduct(p)
    )
    const heldOnly = platinumTradingEnabled
      ? []
      : (summaries ?? [])
          .filter(
            s =>
              s.product &&
              isPlatinumProduct(s.product) &&
              Number(s.currentValue) > 0 &&
              !offered.some(p => p.id === s.product.id)
          )
          .map(s => s.product)
    return [
      ...offered.map(p => withLocals(p, true)),
      ...heldOnly.map(p => withLocals(p, false))
    ]
  }, [platinumTradingEnabled, products, summaries])

  const currentProduct = useMemo(() => {
    if (productsInclLocals) {
      return productsInclLocals.find(item => item.id === productId) ?? null
    }
    return null
  }, [productId, productsInclLocals])

  // A disabled summaries query is never loading, so this only waits when a
  // held-only product could still turn up.
  const loading = productsLoading || configLoading || summariesLoading

  const buyableProducts = useMemo(
    () => productsInclLocals?.filter(p => p.buyable) ?? [],
    [productsInclLocals]
  )

  return {
    // Everything the customer can see: what they are offered, plus metal they
    // hold but may only sell. Buy pickers and flows use `buyableProducts`.
    products: productsInclLocals ?? [],
    buyableProducts,
    loading,
    product: currentProduct,
    productId,
    // The requested product is not offered to this customer (e.g. a deep link
    // to platinum while the flag is off). Pages redirect rather than render a
    // screen for a product that cannot be traded.
    productUnavailable: !loading && Boolean(products) && currentProduct === null
  }
}
